import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, Mail } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { SupplierImportRow } from '@/components/features/suppliers/SupplierImportRow'
import {
  advanceSupplierImport,
  applySupplierImport,
  getCurrentSupplierImport,
  getMailboxes,
  startSupplierImport,
  type ApplySupplierImportResultDto,
  type SupplierImportDto,
  type SupplierOverviewDto,
} from '@/lib/api/v2/planning'
import { ApiError } from '@/lib/api/client'
import { formatDate } from '@/lib/format'
import {
  applyLabel,
  checkedCount,
  draftLines,
  forgetDraft,
  keepDraft,
  isRunning,
  launchSentence,
  rememberPendingImport,
  toApplyLines,
  type ImportLines,
} from '@/lib/supplierImport'

type LineErrors = Record<string, { message: string; suggestedTargetSupplierId: string | null }>

interface SupplierImportPanelProps {
  /** « Retrouver mes fournisseurs dans mes emails » was clicked (or asked by the URL after connecting a mailbox). */
  requested: boolean
  /** Skip the launch sentence: the person already consented by adding the mailbox just now. */
  autoStart: boolean
  suppliers: SupplierOverviewDto[]
  onClosed: () => void
  onApplied: () => void
  /** A line was refused: the page reloads its suppliers so « Ajouter à : X » can name a supplier a colleague just created. */
  onRefused: () => void
  onOpenIgnores: () => void
}

const ADVANCE_PAUSE_MS = 1500

const errorMessage = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback)

/**
 * SOLY-SUPPLIER-002 MS-03 — « Retrouver mes fournisseurs dans mes emails » (03 § S2–S6): one sentence
 * and one button, the progress, then one list ticked in advance where the person only removes what is
 * wrong, and one click. The import runs in slices the page drives; leaving the page and coming back
 * resumes it (or shows its result).
 */
export function SupplierImportPanel({ requested, autoStart, suppliers, onClosed, onApplied, onRefused, onOpenIgnores }: SupplierImportPanelProps) {
  const navigate = useNavigate()
  const [run, setRun] = useState<SupplierImportDto | null>(null)
  const [mailboxes, setMailboxes] = useState<string[] | null>(null)
  const [lines, setLines] = useState<ImportLines>({})
  const [errors, setErrors] = useState<LineErrors>({})
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<ApplySupplierImportResultDto | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)
  const alive = useRef(true)
  const driving = useRef<string | null>(null)

  const show = (dto: SupplierImportDto) => {
    setRun(dto)
    if (dto.status === 'Ready') setLines(draftLines(dto.id, dto.candidates))
  }

  /** Advances the import slice after slice while this page is open. */
  const drive = async (dto: SupplierImportDto) => {
    if (driving.current === dto.id) return
    driving.current = dto.id
    let current = dto
    try {
      while (alive.current && isRunning(current)) {
        const started = Date.now()
        current = await advanceSupplierImport(current.id)
        if (alive.current) show(current)
        // A quick answer is another tab's slice in progress (or a step with nothing to do): wait a
        // little instead of asking again at once.
        if (isRunning(current) && Date.now() - started < ADVANCE_PAUSE_MS) await new Promise((r) => setTimeout(r, ADVANCE_PAUSE_MS))
      }
    } catch (e) {
      if (alive.current) toast.error(errorMessage(e, 'L’import n’a pas pu avancer. Revenez sur la page pour le reprendre.'))
    } finally {
      driving.current = null
    }
  }

  const start = async () => {
    setBusy(true)
    try {
      const dto = await startSupplierImport()
      setDone(null)
      setErrors({})
      show(dto)
      void drive(dto)
    } catch (e) {
      toast.error(errorMessage(e, 'L’import n’a pas pu démarrer.'))
    } finally {
      setBusy(false)
    }
  }

  // Every change of the choices is kept for this import (see draftLines).
  useEffect(() => {
    if (run?.status === 'Ready') keepDraft(run.id, lines)
  }, [run, lines])

  // On arrival (and on « Réessayer »): resume the person's import if one is open, else prepare the launch sentence.
  useEffect(() => {
    alive.current = true
    if (!requested) return
    let active = true
    Promise.all([getCurrentSupplierImport(), getMailboxes()])
      .then(([{ import: current }, boxes]) => {
        if (!active) return
        setMailboxes(boxes.map((b) => b.address))
        if (current && current.status !== 'Applied') {
          show(current)
          void drive(current)
        } else if (boxes.length === 0) {
          // No mailbox yet: the person adds one first, and the import starts once it is connected.
          rememberPendingImport()
          toast.info('Ajoutez d’abord une boîte : l’import démarrera dès qu’elle sera connectée.')
          navigate('/supplier-orders/mailboxes?add=1')
        } else if (autoStart) {
          void start()
        }
      })
      .catch((e) => {
        if (active) setLoadError(errorMessage(e, 'L’import n’a pas pu être chargé.'))
      })
    return () => {
      active = false
      alive.current = false
    }
    // Once per request of the panel, and again on « Réessayer ».
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requested, attempt])

  const apply = async () => {
    if (!run) return
    setBusy(true)
    try {
      const result = await applySupplierImport(run.id, toApplyLines(run.candidates, lines))
      if (!result.applied) {
        // « Tout ou rien, sans rien perdre » — nothing written, every choice kept on screen.
        setErrors(Object.fromEntries(result.errors.map((e) => [e.candidateId, e])))
        toast.error('Rien n’a été ajouté : corrigez les lignes signalées.')
        onRefused()
        return
      }
      setErrors({})
      forgetDraft(run.id)
      setDone(result)
      setRun(null)
      onApplied()
    } catch (e) {
      toast.error(errorMessage(e, 'Les lignes n’ont pas pu être ajoutées.'))
    } finally {
      setBusy(false)
    }
  }

  if (!requested) return null

  if (loadError) {
    return (
      <section className="space-y-3 rounded-md border border-destructive/40 p-4" data-testid="supplier-import-load-error">
        <p className="text-sm">{loadError}</p>
        <div className="flex gap-2">
          <Button
            onClick={() => {
              setLoadError(null)
              setAttempt((n) => n + 1)
            }}
            data-testid="supplier-import-load-retry-btn"
          >
            Réessayer
          </Button>
          <Button variant="ghost" onClick={onClosed} data-testid="supplier-import-close-btn">
            Fermer
          </Button>
        </div>
      </section>
    )
  }

  if (done) {
    const added = done.suppliersAdded
    return (
      <section className="space-y-2 rounded-md border p-4" data-testid="supplier-import-done">
        <p className="font-medium">
          {added + done.suppliersCompleted + done.platformsAdded === 0 && 'Import terminé : rien à ajouter'}
          {added + done.suppliersCompleted + done.platformsAdded > 0 && `${added} fournisseur${added > 1 ? 's' : ''} ajouté${added > 1 ? 's' : ''}`}
          {done.suppliersCompleted > 0 && `, ${done.suppliersCompleted} complété${done.suppliersCompleted > 1 ? 's' : ''}`}
          {done.platformsAdded > 0 && `, ${done.platformsAdded} plateforme${done.platformsAdded > 1 ? 's' : ''}`}
        </p>
        <p className="text-sm text-muted-foreground">
          Avec leurs adresses, le mode « Mail » et « attend une confirmation ». Réglez délais et modes depuis chaque fiche quand vous voulez.
        </p>
        <p className="text-sm text-muted-foreground">Prochain import : seulement les emails arrivés depuis aujourd’hui.</p>
        <Button variant="outline" size="sm" onClick={onClosed} data-testid="supplier-import-close-btn">
          Fermer
        </Button>
      </section>
    )
  }

  if (!run) {
    return (
      <section className="space-y-3 rounded-md border p-4" data-testid="supplier-import-launch">
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-primary" />
          <h2 className="font-semibold">Retrouver mes fournisseurs dans mes emails</h2>
        </div>
        {mailboxes === null ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <>
            <p className="text-sm" data-testid="supplier-import-launch-sentence">{launchSentence(mailboxes)}</p>
            <p className="text-xs text-muted-foreground">
              Les envois faits depuis une boîte non suivie ne sont pas lus : ajoutez-la dans Boîtes mail.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={() => void start()} disabled={busy} data-testid="supplier-import-start-btn">
                Retrouver mes fournisseurs
              </Button>
              <Button variant="ghost" onClick={onClosed} disabled={busy} data-testid="supplier-import-cancel-btn">
                Annuler
              </Button>
              <span className="text-xs text-muted-foreground">Environ une minute. Vous choisissez ensuite qui ajouter.</span>
            </div>
          </>
        )}
      </section>
    )
  }

  if (isRunning(run)) {
    const p = run.progress
    const reading = p?.step !== 'classifying'
    const percent = reading ? 15 : 30 + Math.round((70 * (p?.batchesDone ?? 0)) / Math.max(1, p?.batchesTotal ?? 1))
    return (
      <section className="space-y-3 rounded-md border p-4" data-testid="supplier-import-running">
        <p className="font-medium">Recherche de vos fournisseurs…</p>
        <Progress value={percent} />
        <ul className="space-y-1 text-sm">
          <li className={reading ? 'font-medium' : 'text-muted-foreground'}>
            Lecture des objets des emails · {p?.emailsScanned ?? 0} emails
          </li>
          <li className={reading ? 'text-muted-foreground' : 'font-medium'}>
            Reconnaissance des fournisseurs{!reading && p ? ` · ${p.batchesDone} lot${p.batchesDone > 1 ? 's' : ''} sur ${p.batchesTotal}` : ''}
          </li>
        </ul>
        {run.message && <p className="text-sm text-amber-700">{run.message}</p>}
        <p className="text-xs text-muted-foreground">Vous pouvez quitter la page : le résultat vous attendra ici.</p>
      </section>
    )
  }

  if (run.status !== 'Ready') {
    return (
      <section className="space-y-3 rounded-md border border-destructive/40 p-4" data-testid="supplier-import-failed">
        <p className="text-sm" data-testid="supplier-import-failed-message">{run.message ?? 'L’import n’a pas abouti.'}</p>
        <div className="flex gap-2">
          <Button onClick={() => void start()} disabled={busy} data-testid="supplier-import-retry-btn">
            Relancer
          </Button>
          <Button variant="ghost" onClick={onClosed} data-testid="supplier-import-close-btn">
            Fermer
          </Button>
        </div>
      </section>
    )
  }

  const sure = run.candidates.filter((c) => c.level === 'Sure')
  const unsure = run.candidates.filter((c) => c.level === 'Unsure')
  const count = checkedCount(lines)
  const rows = (list: typeof run.candidates) => (
    <ul className="rounded-md border">
      {list.map((c) => (
        <SupplierImportRow
          key={c.id}
          candidate={c}
          line={lines[c.id]}
          suppliers={suppliers}
          error={errors[c.id]}
          disabled={busy}
          onChange={(l) => setLines((prev) => ({ ...prev, [c.id]: l }))}
        />
      ))}
    </ul>
  )

  return (
    <section className="space-y-4 rounded-md border p-4" data-testid="supplier-import-result">
      <div>
        <p className="font-medium">
          {run.candidates.length === 0
            ? 'Aucun nouveau fournisseur trouvé dans vos emails'
            : `${run.candidates.length} ligne${run.candidates.length > 1 ? 's' : ''} trouvée${run.candidates.length > 1 ? 's' : ''} dans vos emails`}
        </p>
        <p className="text-xs text-muted-foreground">Import du {formatDate(run.createdDate)} · visible de vous seulement</p>
        {run.message && <p className="mt-1 text-sm text-amber-700" data-testid="supplier-import-unread">{run.message}</p>}
      </div>

      {sure.length > 0 && (
        <div className="space-y-2" data-testid="supplier-import-sure">
          <h3 className="text-sm font-semibold">Vos fournisseurs</h3>
          {rows(sure)}
        </div>
      )}
      {unsure.length > 0 && (
        <div className="space-y-2" data-testid="supplier-import-unsure">
          <h3 className="text-sm font-semibold">À vous de voir</h3>
          <p className="text-xs text-muted-foreground">Rien de clair dans les objets : cochez s’il s’agit d’un fournisseur.</p>
          {rows(unsure)}
        </div>
      )}

      <div className="space-y-2 border-t pt-3">
        <div className="flex flex-wrap items-center gap-2">
          {run.candidates.length > 0 ? (
            <Button onClick={() => void apply()} disabled={busy} data-testid="supplier-import-apply-btn">
              {applyLabel(count)}
            </Button>
          ) : (
            // Nothing to decide, but closing it is a decision: the mailboxes are marked read up to now.
            <Button onClick={() => void apply()} disabled={busy} data-testid="supplier-import-finish-btn">
              Terminer
            </Button>
          )}
          <Button variant="ghost" onClick={onClosed} disabled={busy} data-testid="supplier-import-later-btn">
            Plus tard
          </Button>
        </div>
        {run.candidates.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Les lignes décochées ne vous seront plus proposées. Vous les retrouvez dans{' '}
            <button type="button" className="underline" onClick={onOpenIgnores} data-testid="supplier-import-ignores-link">
              Mes ignorés
            </button>
            .
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          Un fournisseur manque ? S’il écrit depuis Gmail ou si vous le commandez par téléphone, ajoutez-le à la main.
        </p>
      </div>
    </section>
  )
}
